export type RegroupingWorkshopStrategy = 'regroup-ones-to-ten';

export interface RegroupingWorkshopConfig {
  readonly id: string;
  readonly skillId: string;
  readonly addendA: number;
  readonly addendB: number;
  readonly strategy: RegroupingWorkshopStrategy;
  readonly target: number;
  readonly completionWorldEvent: string;
}

export interface RegroupingWorkshopPlaceCounts {
  readonly tens: number;
  readonly ones: number;
}

export function decomposeRegroupingWorkshopValue(value: number): RegroupingWorkshopPlaceCounts {
  if (!Number.isInteger(value) || value < 0 || value > 99) {
    throw new RangeError('Regrouping Workshop values must be whole numbers from 0 through 99.');
  }
  return { tens: Math.floor(value / 10), ones: value % 10 };
}

export function validateRegroupingWorkshopConfig(config: RegroupingWorkshopConfig) {
  if (!config.id.trim() || !config.skillId.trim() || !config.completionWorldEvent.trim()) {
    throw new RangeError('Regrouping Workshop identifiers must not be empty.');
  }
  if (config.strategy !== 'regroup-ones-to-ten') {
    throw new RangeError('Regrouping Workshop requires the regroup-ones-to-ten strategy.');
  }
  if (![config.addendA, config.addendB].every(value => (
    Number.isInteger(value) && value >= 10 && value <= 99
  ))) {
    throw new RangeError('Regrouping Workshop addends must be two-digit whole numbers.');
  }
  if (!Number.isInteger(config.target) || config.target < 0 || config.target > 99) {
    throw new RangeError('Regrouping Workshop currently supports totals through 99.');
  }
  if (config.addendA + config.addendB !== config.target) {
    throw new RangeError('Regrouping Workshop target must equal addendA + addendB.');
  }

  const a = decomposeRegroupingWorkshopValue(config.addendA);
  const b = decomposeRegroupingWorkshopValue(config.addendB);
  if (a.ones + b.ones < 10) {
    throw new RangeError('Regrouping Workshop configurations must compose a new ten from the ones.');
  }
  if (a.tens + b.tens + Math.floor((a.ones + b.ones) / 10) > 9) {
    throw new RangeError('This mechanic regroups ones into tens, not tens into a hundred.');
  }

  return config;
}

export const ADDITION_CITADEL_REGROUPING_1 = validateRegroupingWorkshopConfig({
  id: 'addition-citadel-regrouping-1',
  skillId: 'math-addition-two-digit-regroup-ones',
  addendA: 38,
  addendB: 27,
  strategy: 'regroup-ones-to-ten',
  target: 65,
  completionWorldEvent: 'regrouping-workshop-powered-1',
});
